import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button, Row, Col, InputGroup, Form, Spinner, PageHeader } from '@/components/ui';
import { Effort } from '../../../types/Effort';
import { RoutineTask } from '../../../types/RoutineTask';
import { routineTaskService, RoutineTaskListResponse, RoutineTaskFilters } from '../../../services/routineTaskService';
import RoutineWorkTable from '../components/RoutineWorkTable';
import RoutineTaskModal from '../../../components/RoutineTaskModal';
import TaskEffortsTable from '../../../components/TaskEffortsTable';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import RoutineTaskFiltersComponent from '../components/RoutineTaskFilters';
import ServerPagination from '../../../components/ServerPagination';
import { ForemanArea } from '../../../types/ForemanArea';
import { Activity } from '../../../types/Activity';
import { activityService } from '../../../services/activityService';
import TaskHistoryModal from '../../../components/TaskHistoryModal';
import { Task } from '../../../types/Task';
import { useAuth } from '../../../contexts/AuthContext';
import { usePermissions } from '../../../hooks/usePermissions';
import { taskService } from '../../../services/taskService';
import CloneTaskConfirmModal from '../../../components/CloneTaskConfirmModal';
import PrintTaskPDFModal from '../../quantified-tracking/components/PrintTaskPDFModal';
import { scrollListToTopAfterPaint } from '../../../utils/scrollListToTop';

const RoutineWorkPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tasks, setTasks] = useState<RoutineTask[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedTask, setSelectedTask] = useState<RoutineTask | undefined>(undefined);
  const mergeRoutineVehicleFuelEffort = useCallback((effort: Effort) => {
    if (effort.taskId == null) return;
    setTasks((prev) =>
      prev.map((t) =>
        t.id !== effort.taskId && t.taskId !== effort.taskId
          ? t
          : {
              ...t,
              efforts: (t.efforts || []).map((e) => (e.id === effort.id ? { ...e, ...effort } : e))
            }
      )
    );
    setSelectedTask((prev) =>
      prev && (prev.id === effort.taskId || prev.taskId === effort.taskId)
        ? {
            ...prev,
            efforts: (prev.efforts || []).map((e) => (e.id === effort.id ? { ...e, ...effort } : e))
          }
        : prev
    );
  }, []);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedTasks, setSelectedTasks] = useState<number[]>([]);
  const [isHistoryModalVisible, setIsHistoryModalVisible] = useState(false);
  const [printPdfTask, setPrintPdfTask] = useState<Task | null>(null);
  const [selectedTaskForHistory, setSelectedTaskForHistory] = useState<Task | null>(null);
  const { currentUser } = useAuth();
  const { hasPermission } = usePermissions();
  const canCreateTask = hasPermission('routine:specific_create');
  const canCloneTask =
    canCreateTask ||
    hasPermission('project:task_create') ||
    hasPermission('project:task_edit');
  const canReopenTask = hasPermission('project:task_close');
  const canToggleVehicleFuel = hasPermission('quantified:task_edit');
  const [isSearchVisible, setIsSearchVisible] = useState(false);
  const [expandedRows, setExpandedRows] = useState<number[]>([]);
  // Add separate search states
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [cloneConfirmRoutine, setCloneConfirmRoutine] = useState<RoutineTask | null>(null);
  const listScrollRef = useRef<HTMLDivElement>(null);
  const requestIdRef = useRef(0);
  const pendingScrollAfterCreateRef = useRef(false);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 20,
    totalCount: 0,
    totalPages: 0,
    hasNext: false,
    hasPrevious: false
  });
  const [filters, setFilters] = useState<RoutineTaskFilters>({
    status: [],
    foremanArea: [],
    activityName: [],
    priority: '',
    startDate: '',
    endDate: '',
    search: '',
    serviceArea: ''
  });
  const [pendingFilters, setPendingFilters] = useState<RoutineTaskFilters>({
    status: [],
    foremanArea: [],
    activityName: [],
    priority: '',
    startDate: '',
    endDate: '',
    search: '',
    serviceArea: ''
  });
  const [filterOptions, setFilterOptions] = useState<{
    statuses: string[];
    priorities: string[];
    foremanAreas: Array<{ id: number; name: string; }>;
    activities: Array<{ id: number; activityName: string; activityNumber: string; }>;
    serviceAreas: Array<{ id: number; name: string; }>;
  }>({
    statuses: [],
    priorities: [],
    foremanAreas: [],
    activities: [],
    serviceAreas: []
  });

  // Initialize state from URL parameters
  useEffect(() => {
    const pageFromUrl = parseInt(searchParams.get('page') || '1');
    const pageSizeFromUrl = parseInt(searchParams.get('pageSize') || '20');
    
    // Parse array parameters from URL
    const parseArrayParam = (param: string | null): string[] => {
      if (!param) return [];
      return param.split(',').filter(v => v);
    };
    
    const filtersFromUrl: RoutineTaskFilters = {
      status: parseArrayParam(searchParams.get('status')),
      foremanArea: parseArrayParam(searchParams.get('foremanArea')),
      activityName: parseArrayParam(searchParams.get('activityName')),
      priority: searchParams.get('priority') || '',
      startDate: searchParams.get('startDate') || '',
      endDate: searchParams.get('endDate') || '',
      search: searchParams.get('search') || '',
      serviceArea: searchParams.get('serviceArea') || ''
    };
    
    setPagination(prev => ({ ...prev, page: pageFromUrl, pageSize: pageSizeFromUrl }));
    setFilters(filtersFromUrl);
    setPendingFilters(filtersFromUrl);
    // Initialize search states from URL
    setSearchTerm(filtersFromUrl.search || '');
    setSearchInput(filtersFromUrl.search || '');
  }, [searchParams]);

  const taskIdFromUrl = searchParams.get('taskId');
  const returnUrl = searchParams.get('returnUrl') || '';
  const isFromWorkIds = returnUrl === '/work-ids';

  // Fetch tasks when pagination or filters change.
  // When taskId is in URL, include it in search so the task appears in the list (API searches by id).
  useEffect(() => {
    fetchTasks();
  }, [pagination.page, pagination.pageSize, filters, searchTerm, taskIdFromUrl]);

  // Fetch filter options on component mount
  useEffect(() => {
    fetchFilterOptions();
  }, []);

  // Open task modal when taskId is in URL and the task appears in the list.
  // We include taskId in the search (above) so it's fetched; then we open from list results.
  useEffect(() => {
    if (!taskIdFromUrl || tasks.length === 0) return;
    const taskId = parseInt(taskIdFromUrl, 10);
    if (isNaN(taskId)) return;
    const task = tasks.find((t) => t.id === taskId || (t as { taskId?: number }).taskId === taskId);
    if (task) {
      setSelectedTask(task);
      setIsModalVisible(true);
    } else {
      // Fallback: try direct API when task not in current page
      let cancelled = false;
      routineTaskService.getTaskDetails(taskId).then((t) => {
        if (cancelled) return;
        setSelectedTask(t);
        setIsModalVisible(true);
      }).catch((err) => {
        if (!cancelled) console.error('Failed to load task for taskId:', taskId, err);
      });
      return () => { cancelled = true; };
    }
  }, [taskIdFromUrl, tasks]);

  const updateUrlParams = (newPagination?: Partial<typeof pagination>, newFilters?: RoutineTaskFilters) => {
    const params = new URLSearchParams(searchParams);
    const preserveTaskId = searchParams.get('taskId');

    if (newPagination) {
      if (newPagination.page) params.set('page', newPagination.page.toString());
      if (newPagination.pageSize) params.set('pageSize', newPagination.pageSize.toString());
    }

    if (newFilters) {
      Object.entries(newFilters).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          if (value.length > 0) {
            params.set(key, value.join(','));
          } else {
            params.delete(key);
          }
        } else if (value) {
          params.set(key, value as string);
        } else {
          params.delete(key);
        }
      });
    }

    if (preserveTaskId) params.set('taskId', preserveTaskId);
    setSearchParams(params);
  };

  const fetchFilterOptions = async () => {
    try {
      const options = await routineTaskService.getRoutineTaskFilterOptions();
      setFilterOptions(options);
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };

  const fetchTasks = async () => {
    requestIdRef.current += 1;
    const requestId = requestIdRef.current;
    try {
      setLoading(true);
      // When taskId is in URL, filter to only that task and use page 1
      const filterByTaskId = taskIdFromUrl && /^\d+$/.test(taskIdFromUrl);
      const search = filterByTaskId ? (searchTerm || taskIdFromUrl) : searchTerm;
      const combinedFilters = {
        ...filters,
        search,
        ...(filterByTaskId && taskIdFromUrl ? { taskId: taskIdFromUrl } : {})
      };
      const pageToUse = filterByTaskId ? 1 : pagination.page;
      const response: RoutineTaskListResponse = await routineTaskService.getRoutineTasksPaginated(
        pageToUse,
        pagination.pageSize,
        combinedFilters,
        'specific'
      );

      if (requestId !== requestIdRef.current) return;

      setTasks(response.data);
      setPagination(prev => ({ ...prev, ...response.pagination }));

      if (pendingScrollAfterCreateRef.current) {
        pendingScrollAfterCreateRef.current = false;
        scrollListToTopAfterPaint(listScrollRef.current);
      }
    } catch (error) {
      if (requestId !== requestIdRef.current) return;
      console.error('Error fetching tasks:', error);
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  };

  const executeRoutineClone = async (task: RoutineTask, nameOverride?: string) => {
    const taskId = task.taskId ?? task.id;
    try {
      setLoading(true);
      const cloned = await taskService.cloneTask(
        taskId,
        nameOverride != null && nameOverride.trim() !== '' ? { name: nameOverride.trim() } : undefined
      );
      const needle = (cloned.taskNumber || String(cloned.id)).trim();
      const params = new URLSearchParams(searchParams);
      params.set('taskId', String(cloned.id));
      params.set('search', needle);
      params.set('page', '1');
      setSearchParams(params);
    } catch (error) {
      console.error('Error cloning task:', error);
      alert(error instanceof Error ? error.message : 'Failed to clone task');
    } finally {
      setLoading(false);
    }
  };

  const handleTaskAction = async (action: string, task: RoutineTask) => {
    console.log('RoutineWorkPage - handleTaskAction:', { action, taskId: task.id });
    
    switch (action) {
      case 'edit':
        setSelectedTask(task);
        setIsModalVisible(true);
        break;
        
      case 'print-pdf':
        handlePrintTaskPDF(task);
        break;
        
      case 'close':
        try {
          setLoading(true);
          const taskId = task.taskId ?? task.id;
          await routineTaskService.updateTaskStatus(taskId, 'CLOSED', 'Closed from Routine Work page');
          await fetchTasks(); // Reload tasks to get updated data
        } catch (error) {
          console.error('Error closing task:', error);
          const msg = error instanceof Error ? error.message : String(error) || 'Failed to close task';
          alert(msg);
        } finally {
          setLoading(false);
        }
        break;
        
      case 'reopen':
        try {
          setLoading(true);
          const taskId = task.taskId ?? task.id;
          await routineTaskService.updateTaskStatus(taskId, 'ACTIVE', 'Reopened from Routine Work page');
          await fetchTasks(); // Reload tasks to get updated data
        } catch (error) {
          console.error('Error reopening task:', error);
          const msg = error instanceof Error ? error.message : String(error) || 'Failed to reopen task';
          alert(msg);
        } finally {
          setLoading(false);
        }
        break;
        
      case 'cancel':
        try {
          setLoading(true);
          const taskId = task.taskId ?? task.id;
          await routineTaskService.updateTaskStatus(taskId, 'CANCELLED');
          await fetchTasks(); // Reload tasks to get updated data
        } catch (error: unknown) {
          console.error('Error cancelling task:', error);
          const axiosData = (error as { response?: { data?: { error?: string } } })?.response?.data;
          const msg = axiosData?.error ?? ((error instanceof Error ? error.message : String(error)) || 'Failed to cancel task');
          alert(msg);
        } finally {
          setLoading(false);
        }
        break;

      case 'clone-task':
        setCloneConfirmRoutine(task);
        break;

      case 'history':
        // Convert RoutineTask to Task format for the modal
        const taskForHistory: Task = {
          id: task.id,
          type: task.type as any,
          name: task.name || '',
          description: task.description || '',
          status: task.status as any,
          taskHistory: task.taskHistory || [],
          createdAt: task.createdAt ? new Date(task.createdAt) : new Date(),
          updatedAt: new Date(),
          efforts: task.efforts || [],
          activities: task.activities || [],
          taskLocations: task.taskLocations || [],
          foremanArea: task.foremanArea || undefined,
          project: task.project || undefined
        } as Task;
        setSelectedTaskForHistory(taskForHistory);
        setIsHistoryModalVisible(true);
        break;
        
      default:
        console.warn('Unknown action:', action);
    }
  };

  const handleBulkAction = (action: string, tasks: RoutineTask[]) => {
    console.log('Bulk action:', action, tasks);
  };

  const handleTaskClick = (task: RoutineTask) => {
    setSelectedTask(task);
    setIsModalVisible(true);
  };

  const handleTaskSelect = (taskIds: number[]) => {
    setSelectedTasks(taskIds);
  };

  const handleModalClose = () => {
    setSelectedTask(undefined);
    setIsModalVisible(false);
    // Keep taskId in URL so the filter stays applied when modal is closed
  };

  const handleCreateTask = () => {
    setSelectedTask(undefined);
    setIsModalVisible(true);
  };

  const handleTaskSaved = async (updatedTask: RoutineTask) => {
    const isNew = !selectedTask;
    setIsModalVisible(false);
    if (isNew) {
      pendingScrollAfterCreateRef.current = true;
      const urlPage = parseInt(searchParams.get('page') || '1', 10);
      if (urlPage !== 1 || pagination.page !== 1) {
        updateUrlParams({ page: 1 }, filters);
      } else {
        await fetchTasks();
      }
    } else {
      await fetchTasks();
    }
  };

  const handleAddHistoryComment = async (updatedTask: Task) => {
    // Update the task in the local state
    setTasks(prevTasks => 
      prevTasks.map(task => {
        if (task.id === updatedTask.id) {
          return {
            ...task,
            taskHistory: updatedTask.taskHistory || []
          };
        }
        return task;
      })
    );
    // Update the selected task for history if it's the same one
    if (selectedTaskForHistory && selectedTaskForHistory.id === updatedTask.id) {
      setSelectedTaskForHistory(updatedTask);
    }
  };

  const handlePrintTaskPDF = (task: RoutineTask) => {
    setPrintPdfTask(task as unknown as Task);
  };

  const handleFilterChange = (name: string, value: string | string[]) => {
    const newPendingFilters = { ...pendingFilters, [name]: value };
    setPendingFilters(newPendingFilters);
  };

  const handleApplyFilters = () => {
    setFilters(pendingFilters);
    updateUrlParams({ page: 1 }, pendingFilters); // Reset to page 1 when applying filters
  };

  // Search handlers (following Assets page pattern)
  const toggleSearch = () => {
    setIsSearchVisible(prev => !prev);
    if (isSearchVisible) {
      setSearchInput(''); // Clear search when hiding
      setSearchTerm(''); // Clear search when hiding
    }
  };

  const handleSearch = () => {
    const trimmed = searchInput.trim();
    setSearchTerm(trimmed);
    // Update URL params with trimmed search term
    const newFilters = { ...filters, search: trimmed };
    updateUrlParams({ page: 1 }, newFilters);
  };

  const handleSearchInputChange = (value: string) => {
    setSearchInput(value);
  };

  const handleSearchKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const clearSearch = () => {
    setSearchInput('');
    setSearchTerm('');
    // Update URL params without search term
    const newFilters = { ...filters, search: '' };
    updateUrlParams({ page: 1 }, newFilters);
  };

  const handlePageChange = (page: number) => {
    const newPagination = { ...pagination, page };
    setPagination(newPagination);
    updateUrlParams({ page }, filters);
  };

  const handlePageSizeChange = (pageSize: number) => {
    const newPagination = { ...pagination, pageSize, page: 1 };
    setPagination(newPagination);
    updateUrlParams({ pageSize, page: 1 }, filters);
  };

  const handleClearFilters = () => {
    const clearedFilters: RoutineTaskFilters = {
      status: [],
      foremanArea: [],
      activityName: [],
      priority: '',
      startDate: '',
      endDate: '',
      search: '',
      serviceArea: ''
    };
    setPendingFilters(clearedFilters);
    setFilters(clearedFilters);
    updateUrlParams({ page: 1 }, clearedFilters);
  };

  // Check if there are any active filters
  const hasActiveFilters = () => {
    return Object.entries(filters).some(([key, value]) => {
      // Don't count search as a filter since it's handled separately
      if (key === 'search') return false;
      if (Array.isArray(value)) {
        return value.length > 0;
      }
      return value !== '';
    });
  };

  // Check if pending filters are different from applied filters
  const hasUnappliedChanges = () => {
    const compareArrays = (a: string | string[] | undefined, b: string | string[] | undefined): boolean => {
      const arrA = Array.isArray(a) ? a : (a ? [a] : []);
      const arrB = Array.isArray(b) ? b : (b ? [b] : []);
      return arrA.length !== arrB.length || !arrA.every((v, i) => v === arrB[i]);
    };
    
    return compareArrays(pendingFilters.status, filters.status) ||
           compareArrays(pendingFilters.foremanArea, filters.foremanArea) ||
           compareArrays(pendingFilters.activityName, filters.activityName) ||
           pendingFilters.priority !== filters.priority ||
           pendingFilters.startDate !== filters.startDate ||
           pendingFilters.endDate !== filters.endDate ||
           pendingFilters.serviceArea !== filters.serviceArea;
  };

  const toggleRowExpansion = (taskId: number) => {
    setExpandedRows(prev => 
      prev.includes(taskId) 
        ? prev.filter(id => id !== taskId)
        : [...prev, taskId]
    );
  };

  return (
    <div className="routine-work-page tw-w-full tw-p-4 tw-flex tw-flex-col tw-h-full tw-overflow-hidden">
      {/* Back link sits above the title as a breadcrumb rather than competing with it inline. */}
      {isFromWorkIds && (
        <div className="tw-mb-2 tw-shrink-0">
          <Button
            as={Link}
            to={returnUrl}
            variant="link"
            size="sm"
            className="tw-p-0"
            title="Return to Work IDs"
          >
            <i className="bi bi-arrow-left tw-mr-1" aria-hidden />
            Back to Work IDs
          </Button>
        </div>
      )}
      <PageHeader
        title="Routine Work"
        actions={[
          {
            key: 'refresh',
            label: 'Refresh',
            // Was `variant="success"` (green) — Refresh is chrome, not a success outcome.
            icon: 'bi-arrow-clockwise',
            intent: 'neutral',
            onClick: () => {
              setLoading(true);
              Promise.all([
                fetchTasks(),
                fetchFilterOptions()
              ]).finally(() => setLoading(false));
            },
          },
          {
            key: 'search',
            label: `Search${searchTerm ? ` (${searchTerm.length > 20 ? searchTerm.substring(0, 20) + '…' : searchTerm})` : ''}`,
            icon: isSearchVisible ? 'bi-x-lg' : 'bi-search',
            intent: 'secondary',
            active: isSearchVisible || !!searchTerm,
            title: isSearchVisible ? 'Close search' : 'Search tasks',
            onClick: toggleSearch,
          },
          {
            key: 'create',
            label: 'Create Task',
            icon: 'bi-plus-lg',
            intent: 'primary',
            hidden: !canCreateTask,
            title: 'Create new routine task',
            onClick: handleCreateTask,
          },
        ]}
      />

      <div className="tw-shrink-0">
        <RoutineTaskFiltersComponent
          filterOptions={filterOptions}
          filters={pendingFilters}
          onFilterChange={handleFilterChange}
          onClearFilters={handleClearFilters}
          showApplyButton={true}
          onApply={handleApplyFilters}
          applyDisabled={!hasUnappliedChanges()}
          hasActiveFilters={hasActiveFilters()}
        />
      </div>

      {/* Search field appears below filters when visible */}
      {isSearchVisible && (
        <Row className="tw-mb-3 tw-shrink-0">
          <Col xs={12}>
            <InputGroup className="tw-shadow-sm">
              <InputGroup.Text className="tw-bg-primary tw-text-white tw-border-primary">
                <i className="bi bi-search"></i>
              </InputGroup.Text>
              <Form.Control
                type="text"
                className="tw-border-primary/40"
                placeholder="Search by Task Number, Description, Asset, Activity..."
                value={searchInput}
                onChange={(e) => handleSearchInputChange(e.target.value)}
                onKeyDown={(e) => handleSearchKeyPress(e)}
                autoFocus
              />
              <Button
                variant="primary"
                type="button"
                onClick={handleSearch}
                title="Search"
              >
                <i className="bi bi-search"></i>
              </Button>
              {(searchInput || searchTerm) && (
                <Button
                  variant="outline-primary"
                  type="button"
                  onClick={clearSearch}
                  title="Clear search"
                >
                  <i className="bi bi-x-lg"></i>
                </Button>
              )}
            </InputGroup>
            {searchTerm && (
              <div className="tw-text-content-muted tw-text-sm tw-mt-1">
                Searching for: "{searchTerm}"
              </div>
            )}
          </Col>
        </Row>
      )}

      {loading ? (
        <div className="tw-grow tw-flex tw-items-center tw-justify-center" style={{ minHeight: 0 }}>
          <Spinner>Loading...</Spinner>
        </div>
      ) : (
        <>
          <div ref={listScrollRef} className="tw-grow table-scroll-wrapper" style={{ minHeight: 0, minWidth: 0 }}>
            <div className="tw-w-full tw-overflow-x-auto">
              <RoutineWorkTable
                tasks={tasks}
                onTaskAction={handleTaskAction}
                onBulkAction={handleBulkAction}
                onTaskClick={handleTaskClick}
                onTaskSelect={handleTaskSelect}
                selectedTasks={selectedTasks}
                showAsset={true}
                showQuantity={true}
                onVehicleFuelEffortPatched={mergeRoutineVehicleFuelEffort}
                canCloneTask={canCloneTask}
              />
            </div>

            {selectedTask && (
              <div className="tw-mt-4">
                <div className="tw-flex tw-items-center tw-justify-between tw-mb-3">
                  <h3 className="tw-text-xl tw-mb-0">
                    Task Efforts - {selectedTask.taskNumber}
                    <span className="tw-text-content-muted tw-ml-2">{selectedTask.description}</span>
                  </h3>
                  {selectedTask.efforts && selectedTask.efforts.length > 0 && (
                    <Button
                      variant="outline-primary"
                      size="sm"
                      active={expandedRows.includes(selectedTask.id)}
                      onClick={() => toggleRowExpansion(selectedTask.id)}
                      title={expandedRows.includes(selectedTask.id) ? "Hide Efforts" : "Show Efforts"}
                    >
                      <i className={`bi bi-chevron-${expandedRows.includes(selectedTask.id) ? 'up' : 'down'}`}></i>
                    </Button>
                  )}
                </div>
                {expandedRows.includes(selectedTask.id) && (
                  <div className="tw-p-3 tw-bg-surface-alt tw-border tw-border-line tw-rounded">
                    <TaskEffortsTable 
                      efforts={selectedTask.efforts || []}
                      columns={{
                        type: true,
                        date: true,
                        employeeName: true,
                        description: true,
                        accomplishment: true,
                        quantity: true,
                        rate: true,
                        fuelSurcharge: true,
                        totalCost: true,
                        comments: true
                      }}
                      canToggleVehicleFuel={canToggleVehicleFuel}
                      onVehicleFuelEffortPatched={mergeRoutineVehicleFuelEffort}
                    />
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Server-side pagination */}
          <div className="tw-shrink-0">
            <ServerPagination
              currentPage={pagination.page}
              totalPages={pagination.totalPages}
              totalCount={pagination.totalCount}
              pageSize={pagination.pageSize}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              loading={loading}
              className="tw-mt-3 tw-mb-4"
            />
            
            {/* Add extra space at the bottom for mobile devices */}
            <div style={{ height: '20px' }}></div>
          </div>
        </>
      )}

      <RoutineTaskModal
        visible={isModalVisible}
        onClose={handleModalClose}
        onTaskSaved={handleTaskSaved}
        task={selectedTask}
      />

      <CloneTaskConfirmModal
        show={cloneConfirmRoutine !== null}
        onHide={() => setCloneConfirmRoutine(null)}
        onConfirm={(names) => {
          const t = cloneConfirmRoutine;
          setCloneConfirmRoutine(null);
          if (t) void executeRoutineClone(t, names[0]);
        }}
        items={
          cloneConfirmRoutine
            ? [
                {
                  taskId: cloneConfirmRoutine.id,
                  sourceTaskLabel: cloneConfirmRoutine.taskNumber || `#${cloneConfirmRoutine.id}`,
                  sourceTaskName: cloneConfirmRoutine.name
                }
              ]
            : []
        }
      />

      {selectedTaskForHistory && (
        <TaskHistoryModal
          visible={isHistoryModalVisible}
          onClose={() => {
            setIsHistoryModalVisible(false);
            setSelectedTaskForHistory(null);
          }}
          task={selectedTaskForHistory}
          onAddComment={handleAddHistoryComment}
        />
      )}

      <PrintTaskPDFModal
        task={printPdfTask}
        onClose={() => setPrintPdfTask(null)}
      />
    </div>
  );
};

export default RoutineWorkPage; 